import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ClassAnnotation, ClassLiveState } from '../../shared/api/types';
import { Countdown, placeMarks, useClassSocket } from './ClassLive';
import { CameraViewer } from './ClassCamera';

/** Las 4 vistas de la clase en vivo para proyectar. */
export const CLASS_VIEWS = {
  mostrado: 'Lo que muestra el maestro',
  manos: 'Manos levantadas',
  posiciones: 'Tabla de posiciones',
  clase: 'La clase (texto)',
} as const;
export type ClassView = keyof typeof CLASS_VIEWS;

type Screen =
  | { room: { id: string; name: string; kids: boolean }; live: false; now: string }
  | {
      room: { id: string; name: string; kids: boolean };
      live: true;
      now: string;
      sessionId: string;
      topic: string | null;
      page: number;
      pages: number;
      pageText: string;
      marks: ClassAnnotation[];
      shown: ClassAnnotation | null;
      floor: string | null;
      hands: string[];
      pick: string | null;
      teams: string[][] | null;
      timer: { endsAt: string; seconds: number } | null;
      poll: ClassLiveState['poll'];
      standings: { position: number; name: string; points: number }[];
      camera: string | null;
    };

const MEDALS = ['🥇', '🥈', '🥉'];

function youtubeId(url: string): string | null {
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/.exec(url);
  return m ? m[1]! : null;
}

/**
 * La clase en vivo de un salón en la pantalla de Multimedia (fuente «Clase en vivo»). Se actualiza
 * al instante por el socket de la clase y, de respaldo, cada 3 s.
 */
export function ClassScreen({ churchId, classroomId, view, className = '' }: { churchId: string; classroomId: string; view: ClassView; className?: string }) {
  const base = `/organization/churches/${churchId}/classrooms/${classroomId}`;
  const [data, setData] = useState<Screen | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useRef(() => {});
  load.current = () => {
    api
      .get<Screen>(`${base}/live-screen`)
      .then((d) => {
        setData(d);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  };
  useEffect(() => {
    load.current();
    const t = setInterval(() => load.current(), 3000);
    return () => clearInterval(t);
  }, [base]);
  const sessionId = data?.live ? data.sessionId : null;
  const channel = useClassSocket(churchId, classroomId, sessionId, () => load.current());

  if (!data) return <div className={`class-screen ${className}`}>{failed ? <p className="class-screen__status">No se pudo abrir la clase.</p> : null}</div>;
  if (!data.live) {
    return (
      <div className={`class-screen ${className}`}>
        <p className="class-screen__status">La clase de «{data.room.name}» no está en vivo.</p>
      </div>
    );
  }
  const d = data;
  const timer = d.timer ? (
    <div className="class-screen__timer">
      <Countdown endsAt={d.timer.endsAt} now={d.now} big />
    </div>
  ) : null;
  const header = (
    <header className="class-screen__head">
      <span className="live-dot">En vivo</span>
      <strong>{d.topic ?? d.room.name}</strong>
    </header>
  );

  const pageView = (
    <div className="class-screen__text">
      <MarkedPage text={d.pageText.replace(/⟪/g, '(').replace(/⟫/g, ')')} marks={d.marks} />
      {d.pages > 1 ? <span className="class-screen__page">Página {d.page + 1} de {d.pages}</span> : null}
    </div>
  );

  let body;
  if (view === 'manos') {
    body = (
      <div className="class-screen__hands">
        {d.floor ? <p className="class-screen__floor">🎤 Tiene la palabra: {d.floor}</p> : null}
        {d.pick ? <p className="class-screen__pick">🎲 {d.pick}</p> : null}
        <h2>✋ Manos levantadas ({d.hands.length})</h2>
        {d.hands.length === 0 ? <p className="class-screen__muted">Nadie ha levantado la mano.</p> : null}
        <ol>
          {d.hands.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ol>
        {d.teams ? (
          <div className="class-screen__teams">
            {d.teams.map((t, i) => (
              <div key={i}>
                <strong>Equipo {i + 1}</strong>
                <span>{t.join(', ')}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  } else if (view === 'posiciones') {
    body = (
      <div className="class-screen__standings">
        <h2>Tabla de posiciones · este mes</h2>
        <ol>
          {d.standings.map((e) => (
            <li key={e.name} className={e.position <= 3 && e.points > 0 ? 'top' : undefined}>
              <span className="class-screen__place">{e.points > 0 && e.position <= 3 ? MEDALS[e.position - 1] : `${e.position}.º`}</span>
              <span className="class-screen__name">{e.name}</span>
              <span key={e.points} className="class-screen__points">
                {e.points} pts
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  } else if (view === 'mostrado' && d.shown) {
    const yt = d.shown.url ? youtubeId(d.shown.url) : null;
    body =
      d.shown.kind === 'image' && d.shown.documentId ? (
        <img className="class-screen__media" src={`/api${base}/documents/${d.shown.documentId}/file?ver=1`} alt="" />
      ) : d.shown.url ? (
        <iframe className="class-screen__media" src={yt ? `https://www.youtube.com/embed/${yt}?autoplay=1` : d.shown.url} title="Lo que muestra el maestro" allow="autoplay; encrypted-media" sandbox="allow-scripts allow-same-origin allow-presentation" />
      ) : null;
  } else if (view === 'mostrado' && d.camera) {
    body = (
      <div className="class-screen__camera">
        <CameraViewer channel={channel} name={d.camera} className="class-screen__media" startMuted={false} />
        <p className="class-screen__floor">📷 {d.camera}</p>
      </div>
    );
  } else if (view === 'mostrado' && d.poll) {
    body = (
      <div className="class-screen__poll">
        <h2>{d.poll.question}</h2>
        {d.poll.options.map((o, i) => {
          const pct = d.poll!.total ? Math.round((d.poll!.counts[i]! / d.poll!.total) * 100) : 0;
          return (
            <div key={i} className="class-screen__bar" style={{ backgroundSize: `${pct}% 100%` }}>
              <span>{o}</span>
              <span>
                {d.poll!.counts[i]} · {pct}%
              </span>
            </div>
          );
        })}
        <p className="class-screen__muted">{d.poll.total} respuestas</p>
      </div>
    );
  } else if (view === 'mostrado' && d.pick) {
    body = <p className="class-screen__pick class-screen__pick--big">🎲 {d.pick}</p>;
  } else {
    body = pageView;
  }

  return (
    <div className={`class-screen${d.room.kids ? ' class-screen--kids' : ''} ${className}`}>
      {view === 'mostrado' && d.shown ? null : header}
      {body}
      {timer}
    </div>
  );
}

function MarkedPage({ text, marks }: { text: string; marks: ClassAnnotation[] }) {
  const placed = placeMarks(text, marks);
  const parts = [];
  let at = 0;
  for (const m of placed) {
    if (m.start > at) parts.push(text.slice(at, m.start));
    parts.push(
      <mark key={m.a.id} className={`hl hl--${m.a.color ?? 'amarillo'}`}>
        {text.slice(m.start, m.end)}
      </mark>,
    );
    at = m.end;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <p className="class-screen__body">{parts}</p>;
}

/** La misma vista a pantalla completa en el navegador (p. ej. una tele sin la app de Multimedia). */
export function ClassScreenPage() {
  const { churchId = '', classroomId = '', view = 'mostrado' } = useParams();
  const v = (view in CLASS_VIEWS ? view : 'mostrado') as ClassView;
  return (
    <div className="class-screen-page">
      <ClassScreen churchId={churchId} classroomId={classroomId} view={v} />
    </div>
  );
}
